/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/*
 * edited.component.ts
 * 25-04-2024 - Jelmer Jellema - Spin in het Web B.V. Apeldoorn NL
 *
 * Toon content die met de sihw-editor gemaakt is.
 *
 * GEBRUIK:
 *   <sihw-edited [content]="..."></sihw-edited>
 *
 * INPUTS:
 *   content: any
 *     - De te tonen content. Standaard een Quill Delta-object (aanbevolen).
 *       Als [useHtml]="true" wordt een HTML-string verwacht.
 *
 *   useHtml: boolean (standaard false)
 *     - false (standaard): content is een Quill Delta-object; wordt gerenderd via <quill-view>
 *     - true: content is een HTML-string; wordt gerenderd via <quill-view-html> (legacy)
 *     Gebruik dezelfde waarde als bij de bijbehorende <sihw-editor>.
 *
 *   inline: boolean (standaard false)
 *     - Zet het buitenste element op display: inline-block
 *
 *   geenbuitenmarge: boolean (standaard false)
 *     - Verwijdert de buitenmarge van de weergegeven content (eerste en laatste element)
 *
 * AFHANKELIJKHEDEN:
 *   quill en ngx-quill moeten geïnstalleerd zijn in het host-project.
 *   De host-applicatie moet ook de Quill-stylesheet zelf laden. Voeg het volgende toe aan
 *   angular.json → projects → architect → build → options → styles:
 *     "node_modules/quill/dist/quill.snow.css"
 *   Of importeer het in styles.scss:
 *     @import 'quill/dist/quill.snow.css';
 *
 * COMBINEER MET:
 *   <sihw-editor> voor het bewerken van de content. Gebruik dezelfde waarde
 *   voor [useHtml] in zowel editor als edited.
 */

import {
  AfterViewInit,
  booleanAttribute,
  Component,
  ElementRef,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';
import {BaseComponent} from '../base/basecomponent';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {QuillModule} from 'ngx-quill';

@Component({
    standalone: true,
    selector: 'sihw-edited',
    templateUrl: './edited.component.html',
    styleUrls: ['./edited.component.scss'],
    imports: [QuillModule]
})
export class EditedComponent extends BaseComponent implements AfterViewInit {
  @Input({transform: booleanAttribute}) inline: boolean = false;
  @Input({transform: booleanAttribute}) geenbuitenmarge: boolean = false;
  @Input({transform: booleanAttribute}) useHtml: boolean = false;
  @ViewChild('container') container!: ElementRef;
  public trustedcontent: SafeHtml = '';

  constructor(private sanitizer: DomSanitizer, private renderer: Renderer2) {
    super();
  }

  private _content: any = null;

  get content(): any {
    return this._content;
  }

  @Input('content') set content(c: any) {
    this._content = c ?? null;
    this.updateTrusted();
  }

  ngAfterViewInit() {
    if (this.inline && this.container) {
      const buitenste = this.container.nativeElement.firstElementChild;
      if (buitenste) {
        this.renderer.setStyle(buitenste, 'display', 'inline-block');
      }
    }
  }

  private updateTrusted() {
    if (!this.useHtml) {
      // Delta-modus: geen HTML-verwerking nodig, quill-view doet het renderen
      return;
    }
    const c: string = (this._content as string) ?? '';
    this.trustedcontent = this.sanitizer.bypassSecurityTrustHtml(c);
  }
}
